/**
 * screens/dashboard.tsx — Operations dashboard (rev 5), the engine home.
 * Faithful port of the design prototype's `_dashboardVM` / dashboard template:
 * kicker + "Operations dashboard" title + "+ Add queue", a dark "Busiest queue"
 * callout, six KPI cards, and a per-queue utilisation table (QUEUE · UTILISATION
 * · IN LINE · PEAK · WAIT NOW · MAX WAIT · SERVED). Reads GET /v1/dashboard.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Mono } from '../queue/components/atoms';
import { SurfacePage } from '../lib/surfaceLayout';
import { CONTENT } from '../lib/responsive';
import { useActiveQueue } from '../queue/activeQueue';
import { useSurfaceRouter } from '../navigation/surfaceRouter';
import { useDataSource } from '../queue/dataSource';
import { getDashboard } from '../services/queue/queueService';
import { handleApiError } from '../utils/errorHandler';
import CreateQueueDialog from '../queue/components/createQueueDialog';
import type { DashboardView, DashboardQueueRow } from '../api/queueDtos';

/** "0m" | "18m" | "1h 14m" — matches the prototype's fmt(). */
function fmtMin(seconds: number): string {
  const m = Math.max(0, Math.round(seconds / 60));
  if (m <= 0) return '0m';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function DashboardScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { mode } = useDataSource();
  const { queues, setActiveQueue } = useActiveQueue();
  const { setSurface } = useSurfaceRouter();
  const [data, setData] = useState<DashboardView | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    if (mode !== 'live') { setData(null); return; }
    getDashboard().then(setData).catch((err) => handleApiError(err, 'Could not load dashboard'));
  }, [mode]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // live rollup
    return () => clearInterval(t);
  }, [load]);

  const rows: DashboardQueueRow[] =
    data?.queues ??
    queues.map((q) => ({
      id: q.id, name: q.name, product: q.product, matchType: q.matchType, feature: null,
      capacity: 0, used: 0, inLine: 0, inService: 0, utilisation: 0,
      peakInLineToday: 0, maxWaitTodaySeconds: 0, servedToday: 0,
    }));

  // Busiest-first ordering (prototype sorts cards by utilisation desc).
  const sorted = [...rows].sort((a, b) => b.utilisation - a.utilisation || b.inLine - a.inLine);
  const busiestId = data?.busiestQueueId ?? sorted[0]?.id ?? null;
  const busiestRow = sorted.find((r) => r.id === busiestId) ?? null;
  // "Busiest" is only meaningful under real load — when every queue is idle
  // (0 utilisation, nobody in line) there is no busiest; don't crown one.
  const busiest = busiestRow && (busiestRow.utilisation > 0 || busiestRow.inLine > 0) ? busiestRow : null;
  const k = data?.kpis;

  const open = (id: string) => { setActiveQueue(id); setSurface('detail'); };

  const kpis = [
    { k: 'Live queues', v: String(k?.liveQueues ?? rows.length) },
    { k: 'Total in line', v: String(k?.totalInLine ?? 0) },
    { k: 'Peak in line · today', v: String(k?.peakInLineToday ?? 0) },
    { k: 'Max wait · today', v: fmtMin(k?.maxWaitTodaySeconds ?? 0) },
    { k: 'Served · today', v: String(k?.servedToday ?? 0) },
    { k: 'Avg utilisation', v: `${Math.round((k?.avgUtilisation ?? 0) * 100)}%` },
  ];

  return (
    <SurfacePage maxWidth={1180}>
      <View style={{ width: '100%', maxWidth: 1180 }}>
        {/* Header row: title block (left) + Busiest callout (right). */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <View>
            <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>Neutral engine · all queues</Mono>
            <Text style={{ fontSize: 26, fontWeight: '700', marginTop: 4, color: colors.ink }}>Operations dashboard</Text>
            <Text style={{ fontSize: 13.5, color: colors.ink3, marginTop: 4 }}>Utilisation, wait and load across every queue the engine runs — live.</Text>
            <TouchableOpacity
              onPress={() => setShowCreate(true)}
              style={{ marginTop: 13, height: 40, paddingHorizontal: 18, borderRadius: 10, backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start' }}
            >
              <Text style={{ color: colors.white, fontWeight: '600', fontSize: 14 }}>+ Add queue</Text>
            </TouchableOpacity>
          </View>

          {/* Busiest queue callout — only shown when a queue actually has load
              (no "busiest" among idle queues). Name already includes the feature. */}
          {busiest && (
            <View style={{ backgroundColor: colors.ink, borderRadius: 14, paddingVertical: 14, paddingHorizontal: 20, minWidth: 210 }}>
              <Text style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, color: colors.ink4 }}>Busiest queue</Text>
              <Text style={{ fontSize: 15, fontWeight: '700', marginTop: 4, lineHeight: 18, color: colors.white }}>
                {busiest.name}
              </Text>
              <Mono style={{ fontSize: 22, fontWeight: '600', marginTop: 4, color: colors.white }}>
                {`${Math.round(busiest.utilisation * 100)}%`}
                <Text style={{ fontSize: 12, color: colors.ink4, fontWeight: '400' }}>  utilised</Text>
              </Mono>
            </View>
          )}
        </View>

        {/* KPI cards */}
        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {kpis.map((kpi) => (
            <View key={kpi.k} style={{ flexGrow: 1, minWidth: 150, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 13, paddingVertical: 16, paddingHorizontal: 18 }}>
              <Mono style={{ fontSize: 28, fontWeight: '600', lineHeight: 30, color: colors.ink }}>{kpi.v}</Mono>
              <Text style={{ fontSize: 11, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 6 }}>{kpi.k}</Text>
            </View>
          ))}
        </View>

        {/* Registry table */}
        <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' }}>
          <TableHeader />
          {sorted.length === 0 ? (
            <View style={{ padding: 24, alignItems: 'center' }}>
              <Text style={{ color: colors.ink4 }}>No queues yet. Add one to get started.</Text>
            </View>
          ) : (
            sorted.map((r) => <TableRow key={r.id} row={r} busiest={busiest?.id === r.id} onPress={() => open(r.id)} />)
          )}
        </View>

        <Text style={{ marginTop: 14, fontSize: 12, color: colors.ink4 }}>
          Tap any queue to open its detail — live line, resources, and per-queue Controls &amp; Setup. Utilisation = resources (or slots/agents) in use ÷ available capacity.
        </Text>
      </View>

      <CreateQueueDialog open={showCreate} onClose={() => { setShowCreate(false); load(); }} />
    </SurfacePage>
  );
}

function TableHeader() {
  const { theme } = useTheme();
  const { colors } = theme;
  const cell = { fontSize: 10, fontWeight: '600' as const, textTransform: 'uppercase' as const, letterSpacing: 0.5, color: colors.ink4 };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: colors.paper }}>
      <Text style={[cell, { flex: 1.4 }]}>Queue</Text>
      <Text style={[cell, { flex: 1.3 }]}>Utilisation</Text>
      <Text style={[cell, { width: 58, textAlign: 'center' }]}>In line</Text>
      <Text style={[cell, { width: 50, textAlign: 'center' }]}>Peak</Text>
      <Text style={[cell, { width: 74, textAlign: 'center' }]}>Wait now</Text>
      <Text style={[cell, { width: 78, textAlign: 'center' }]}>Max wait</Text>
      <Text style={[cell, { width: 64, textAlign: 'center' }]}>Served</Text>
      <View style={{ width: 20 }} />
    </View>
  );
}

function TableRow({ row, busiest, onPress }: { row: DashboardQueueRow; busiest: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const fitted = row.matchType === 'FITTED';
  const utilPct = Math.round(row.utilisation * 100);
  // Bar shade steps with load, like the prototype (≥90 ink, ≥60 ink3, else ink4).
  const barColor = utilPct >= 90 ? colors.ink : utilPct >= 60 ? colors.ink2 : colors.ink3;
  // Capacity label: product-specific noun (tables / rooms / slots / agents).
  const capNoun = fitted ? 'tables' : row.product === 'VOICE' ? 'slots' : 'agents';
  const capLabel = row.capacity > 0 ? `${row.used}/${row.capacity} ${capNoun}` : '—';
  const waitLabel = fitted ? 'est. wait' : row.product === 'VOICE' ? 'longest hold' : 'longest wait';
  // Badge shows the ACTUAL product so Voice vs Chat is obvious at a glance
  // (was generic "POOL" for both). WAITLIST reads as FITTED (its match type).
  const badgeLabel = row.product === 'WAITLIST' ? 'FITTED' : row.product;

  return (
    <TouchableOpacity
      onPress={onPress}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 15, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: busiest ? colors.paper : colors.card }}
    >
      {/* Queue name + badges + feature */}
      <View style={{ flex: 1.4, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink }} numberOfLines={1}>{row.name}</Text>
          <View style={{ height: 19, paddingHorizontal: 8, borderRadius: 5, justifyContent: 'center', backgroundColor: fitted ? colors.ink : colors.card, borderWidth: fitted ? 0 : 1, borderColor: colors.ink }}>
            <Mono style={{ fontSize: 10, fontWeight: '600', color: fitted ? colors.white : colors.ink }}>{badgeLabel}</Mono>
          </View>
          {busiest && (
            <View style={{ borderWidth: 1, borderColor: colors.ink, borderRadius: 5, paddingHorizontal: 6, paddingVertical: 1 }}>
              <Mono style={{ fontSize: 9, fontWeight: '600', color: colors.ink }}>BUSIEST</Mono>
            </View>
          )}
        </View>
        <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }} numberOfLines={1}>{row.feature ?? row.product}</Text>
      </View>

      {/* Utilisation bar + % + capacity label */}
      <View style={{ flex: 1.3, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View style={{ flex: 1, height: 9, backgroundColor: colors.paper3, borderRadius: 999, overflow: 'hidden' }}>
          <View style={{ height: '100%', width: `${Math.min(100, utilPct)}%`, backgroundColor: barColor, borderRadius: 999 }} />
        </View>
        <Mono style={{ fontSize: 14, fontWeight: '600', width: 40, textAlign: 'right', color: colors.ink }}>{utilPct}%</Mono>
        <Mono style={{ fontSize: 10.5, color: colors.ink4, width: 88 }}>{capLabel}</Mono>
      </View>

      <Mono style={{ width: 58, textAlign: 'center', fontSize: 16, fontWeight: '600', color: colors.ink }}>{row.inLine}</Mono>
      <Mono style={{ width: 50, textAlign: 'center', fontSize: 14, color: colors.ink2 }}>{row.peakInLineToday}</Mono>
      <View style={{ width: 74, alignItems: 'center' }}>
        <Mono style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{row.maxWaitTodaySeconds > 0 ? fmtMin(row.maxWaitTodaySeconds) : 'now'}</Mono>
        <Text style={{ fontSize: 9, color: colors.ink4 }}>{waitLabel}</Text>
      </View>
      <Mono style={{ width: 78, textAlign: 'center', fontSize: 13.5, color: colors.ink2 }}>{fmtMin(row.maxWaitTodaySeconds)}</Mono>
      <Mono style={{ width: 64, textAlign: 'center', fontSize: 14, color: colors.ink2 }}>{row.servedToday}</Mono>
      <Text style={{ width: 20, textAlign: 'right', color: colors.ink4, fontSize: 13 }}>▸</Text>
    </TouchableOpacity>
  );
}
